import React, { createContext, useState, useContext, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { useAppState } from "../../../context/StateContext";
import { PurchaseOrder } from "../../../models/purchase.order";
import { TolerancePercentage } from "../../../models/tolerancePercentage";
import PurchaseOrderService from "../../../services/purchase.order.service";
import { PurchaseOrderKeys } from "../../../util/queries";
import { useBookingContext } from "./Booking";

interface BookingResourcesContextProps {
  orderList: PurchaseOrder[];
  refetchOrderList: () => void;
  tolerance?: TolerancePercentage | undefined;
  isLoading: boolean;
  isExporterInfoDifferentFromVendor?: boolean;
  setIsExporterInfoDifferentFromVendor: React.Dispatch<React.SetStateAction<boolean>>;
}

const BookingResourcesContext = createContext<BookingResourcesContextProps>(
  {} as BookingResourcesContextProps
);

interface BookingResourcesProviderProps {
  children: React.ReactChild;
}

const BookingResourcesProvider = ({ children }: BookingResourcesProviderProps) => {
  const { currentUser } = useAppState();
  const { booking } = useBookingContext();

  const [isExporterInfoDifferentFromVendor, setIsExporterInfoDifferentFromVendor] =
    useState<boolean>(false);

  const filters = useMemo(() => {
    return {
      ...(booking?.shipmentType ? { mode: [booking?.shipmentType] } : {}),
      ...(booking?.portOfLoadingId ? { originPortId: [booking?.portOfLoadingId?.toString()] } : {}),
      ...(booking?.portOfDischargeId
        ? { destinationPortId: [booking?.portOfDischargeId?.toString()] }
        : {}),
      ...(booking?.cargoReadyDate ? { cargoReadyDate: [booking?.cargoReadyDate] } : {}),
      ...(booking?.purchaseOrders && booking?.purchaseOrders?.length > 0
        ? { orders: [...booking.purchaseOrders.map((order) => String(order.id))] }
        : {}),
    };
  }, [
    booking?.shipmentType,
    booking?.portOfLoadingId,
    booking?.portOfDischargeId,
    booking?.cargoReadyDate,
    booking?.purchaseOrders,
  ]);

  const includeAvailablePurchaseOrders = JSON.stringify([
    { association: "supplier", attributes: ["name"], include: [{ association: "location" }] },
    { association: "consignee", attributes: ["name"] },
    { association: "purchaseOrderProducts", include: [{ association: "product" }] },
  ]);

  const {
    data: orderList,
    isInitialLoading,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: PurchaseOrderKeys.getAvailable([
      filters?.mode,
      filters?.originPortId,
      filters?.destinationPortId,
      filters?.cargoReadyDate,
      !!filters?.orders,
      currentUser?.companyId,
    ]),
    queryFn: async () => {
      const response = await PurchaseOrderService.getAvailableList({
        companyId: currentUser?.companyId,
        include: includeAvailablePurchaseOrders,
        ...filters,
      });

      if (response && response.data) return response.data.rows;
    },
    enabled:
      !!currentUser?.companyId &&
      !!filters?.mode &&
      !!filters?.originPortId &&
      !!filters?.destinationPortId &&
      !!filters?.cargoReadyDate &&
      !!filters?.orders,
  });

  return (
    <BookingResourcesContext.Provider
      value={{
        orderList: orderList || [],
        isLoading: isInitialLoading || isRefetching,
        refetchOrderList: refetch,
        isExporterInfoDifferentFromVendor,
        setIsExporterInfoDifferentFromVendor,
      }}
    >
      {children}
    </BookingResourcesContext.Provider>
  );
};

export { BookingResourcesContext, BookingResourcesProvider };

export const useBookingResources = () => {
  const context = useContext(BookingResourcesContext);

  return context;
};
